// src/components/InternalActionItemListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import DetailedListView from './DetailedListView';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { actionItemQueries, initiativeQueries, departmentQueries } from '../repository/graphQueries'; 
import { formatDate, formatScore } from '../utils'; 
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';
import { DRI_EXPLANATION } from '../constants';

const actionItemColumns = [
    { header: 'Name', key: 'name' },
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Deadline', key: 'deadline', formatter: formatDate },
    { header: 'Initiative', key: 'initiativeNames', formatter: names => Array.isArray(names) ? names.join(', ') : names },
    { header: 'Urgency Score', key: 'score', align: 'center', formatter: formatScore }, 
    { header: 'Status', key: 'status' },
    { header: 'Description', key: 'description', type: 'longText' }
];

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative inline-block text-left">
            <div>
                <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                    checked={selectedOptions.includes(option)}
                                    onChange={() => onChange(option)}
                                />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Tooltip component
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={() => setIsVisible(!isVisible)} // For mobile support
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const InternalActionItemListView = () => {
    const { neo4jService, departmentId: departmentIdFromContext, dateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [actionItemsList, setActionItemsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    
    const [actionItemAssigneeFilter, setActionItemAssigneeFilter] = useState('');
    const [assigneeList, setAssigneeList] = useState([]);
    const [loadingAssignees, setLoadingAssignees] = useState(false);

    const [actionItemInitiativeFilter, setActionItemInitiativeFilter] = useState('');
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    const [loadingInitiativesForFilter, setLoadingInitiativesForFilter] = useState(false);
    
    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(true);
    const [selectedDris, setSelectedDris] = useState([]);

    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !selectedDepartment) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(departmentQueries.getDepartmentDRIs, { departmentId: selectedDepartment });
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const loadFilterOptions = useCallback(async () => {
        if (!neo4jService || !departmentIdFromContext) return;
        setLoadingAssignees(true); setLoadingInitiativesForFilter(true);
        setAssigneeList([]); setInitiativeListForFilter([]);
        try {
            const [assigneeResults, initiativeResults] = await Promise.all([
                 neo4jService.runQuery(actionItemQueries.getDistinctAssigneesByDepartment, { departmentId: departmentIdFromContext }),
                 neo4jService.runQuery(initiativeQueries.getDistinctInitiativesByDepartment, { departmentId: departmentIdFromContext })
            ]);
            setAssigneeList(assigneeResults.map(r => r.assignee).filter(a => a)); // Filter out null/empty assignees
            setInitiativeListForFilter(initiativeResults.map(r => r.initiativeName).filter(name => name)); // Filter out null/empty names
        } catch (err) { console.error("Error loading filter options:", err); }
        finally { setLoadingAssignees(false); setLoadingInitiativesForFilter(false); }
    }, [neo4jService, departmentIdFromContext]);

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!neo4jService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [neo4jService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (departmentIdFromContext) {
            loadFilterOptions();
        }
    }, [loadFilterOptions, departmentIdFromContext])

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleAssigneeFilterChange = (event) => setActionItemAssigneeFilter(event.target.value);
    const handleInitiativeFilterChange = (event) => setActionItemInitiativeFilter(event.target.value);

    const handleDepartmentChange = (event) => {
        const newDept = event.target.value;
        navigate(`/internal/${newDept}/action-items`);
    };

    const loadDepartmentActionItems = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoading(true); setError(null);
        try {
            const params = { 
                departmentId: selectedDepartment,
                startDate: dateRange.start,
                endDate: dateRange.end,
                assigneeFilter: actionItemAssigneeFilter || null,
                initiativeFilter: actionItemInitiativeFilter || null,
                driFilter: selectedDris.length > 0 ? selectedDris : null,
            };
            const items = await neo4jService.runQuery(departmentQueries.getDepartmentActionItemsList, params); 
            setActionItemsList(items);
        } catch (err) { setError(err.message); setActionItemsList([]); }
        finally { setLoading(false); }
    }, [neo4jService, selectedDepartment, dateRange, actionItemAssigneeFilter, actionItemInitiativeFilter, selectedDris]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentActionItems();
        }
    }, [selectedDepartment, loadDepartmentActionItems]);

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Department Action Items</h2>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                        <DropdownCheckbox
                            options={dris}
                            selectedOptions={selectedDris}
                            onChange={handleDriChange}
                            title="Filter by DRI"
                        />
                        <Tooltip content={driTooltipContent}>
                            <InformationCircleIcon className="h-4 w-4 text-gray-500 ml-1 cursor-help" />
                        </Tooltip>
                    </div>
                    {departments.length > 0 && (
                        <>
                            <label htmlFor="department-select-actions" className="text-sm font-medium text-gray-700">
                                Department:
                            </label>
                            <select
                                id="department-select-actions"
                                value={selectedDepartment}
                                onChange={handleDepartmentChange}
                                className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                disabled={loadingDepartments || !departments.length}
                            >
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>
            
            <div className="mb-8">
                <EisenhowerMatrixView 
                    departmentId={selectedDepartment} 
                    clientId={null}
                    typeFilter={['ActionItem']}
                    driFilter={selectedDris}
                />
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="assigneeFilter" className="block text-sm font-medium text-gray-700 mb-1">Assignee:</label>
                    <select id="assigneeFilter" value={actionItemAssigneeFilter} onChange={handleAssigneeFilterChange} disabled={loadingAssignees} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                        <option value="">All</option>
                        {assigneeList.map(a => (<option key={a} value={a}>{a || 'Unassigned'}</option>))}
                    </select>
                    {loadingAssignees && <p className="text-xs text-gray-500 mt-1">...</p>}
                </div>
                
                <div>
                    <label htmlFor="initiativeFilter" className="block text-sm font-medium text-gray-700 mb-1">Initiative:</label>
                    <select id="initiativeFilter" value={actionItemInitiativeFilter} onChange={handleInitiativeFilterChange} disabled={loadingInitiativesForFilter} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                        <option value="">All</option>
                        {initiativeListForFilter.map(name => (<option key={name} value={name}>{name}</option>))}
                    </select>
                    {loadingInitiativesForFilter && <p className="text-xs text-gray-500 mt-1">...</p>}
                </div>
            </div>
            
            <DetailedListView
                title="Pending Action Items"
                items={actionItemsList}
                columns={actionItemColumns}
                isLoading={loading}
                error={error}
                entityType="action-items"
            />
        </>
    );
};

export default InternalActionItemListView;